import React, { type ReactElement, useCallback } from 'react';
import { Button as FlowbiteButton, Checkbox, Dropdown } from 'flowbite-react';
import { type ComponentSize, type SelectMenuItem } from './__commons__/selectCommons';
import { dropdownTheme } from '../../styles/themes/select';
import { Text, TextProps } from '../text';
import { buttonIconTheme, selectButtonTheme } from '../../styles/themes/button';
import { IconLoader } from '../iconLoader';

export interface SelectProps {
    value?: string;
    valueClassName?: string;
    valueVariant?: TextProps['variant'];
    middleMenuClassName?: string;
    middleMenuItems: SelectMenuItem[];
    bottomMenuItems?: SelectMenuItem[];
    label?: string;
    helper?: string;
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'left-start' | 'right-start';
    testId?: string;
    displaySelectedIcon?: boolean;
    variant?: string;
    size?: ComponentSize;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    disabled?: boolean;
    header?: header;
    onItemSelect: (item: SelectMenuItem) => void;
    id: string;
    pill?: boolean;
    arrow?: boolean;
    renderTrigger?: () => ReactElement;
    onClear?: () => void;
    buttonClassName?: string;
    multiSelect?: boolean;
    onSelectAll?: (items: SelectMenuItem[]) => void;
    disableSelectOptions?: boolean;
}

export interface header {
    title: string;
    subtitle: string;
}

const checkboxClass =
    'mr-2 bg-system-surface-container checked:bg-system-primary checked:text-system-on-primary border border-system-outline focus:ring-0 focus:outline-none';

export const Select: React.FC<SelectProps> = (props) => {
    const {
        value,
        valueClassName,
        valueVariant,
        middleMenuItems,
        bottomMenuItems,
        middleMenuClassName = '',
        label,
        helper,
        displaySelectedIcon = true,
        placement = 'bottom',
        testId,
        disabled,
        variant = 'primary',
        size = 'sm',
        header,
        onItemSelect,
        id,
        startIcon,
        endIcon,
        pill,
        arrow = true,
        renderTrigger,
        onClear,
        buttonClassName,
        multiSelect = false,
        onSelectAll,
        disableSelectOptions = false,
    } = props;

    const allSelected = middleMenuItems.every((item) => item.isSelected);
    const hasSelectedItems = middleMenuItems.some((item) => item.isSelected);

    const handleSelectAll = useCallback(() => {
        const newSelectionState = !allSelected;
        onSelectAll?.(middleMenuItems.map((item) => ({ ...item, isSelected: newSelectionState })));
    }, [allSelected, middleMenuItems, onSelectAll]);

    const renderArrow = useCallback(
        (buttonElement: HTMLButtonElement | null) => {
            const arrowClass = 'stroke-system-on-surface-variant fill-system-on-surface-variant';

            if (placement.includes('bottom')) {
                return (
                    <>
                        {buttonElement?.ariaExpanded === 'true' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronUp'} />
                        )}
                        {buttonElement?.ariaExpanded === 'false' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronDown'} />
                        )}
                    </>
                );
            }
            if (placement.includes('top')) {
                return (
                    <>
                        {buttonElement?.ariaExpanded === 'true' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronDown'} />
                        )}
                        {buttonElement?.ariaExpanded === 'false' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronUp'} />
                        )}
                    </>
                );
            }
            if (placement.includes('left')) {
                return (
                    <>
                        {buttonElement?.ariaExpanded === 'true' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronRight'} />
                        )}
                        {buttonElement?.ariaExpanded === 'false' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronLeft'} />
                        )}
                    </>
                );
            }
            if (placement.includes('right')) {
                return (
                    <>
                        {buttonElement?.ariaExpanded === 'true' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronLeft'} />
                        )}
                        {buttonElement?.ariaExpanded === 'false' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronRight'} />
                        )}
                    </>
                );
            }
        },
        [placement],
    );

    const dropdownRenderButtonTrigger = useCallback(() => {
        // Arrow issue workaround
        const triggerId = `${id}-dropdownTrigger`;
        const buttonElement = document.getElementById(triggerId) as HTMLButtonElement | null;

        return (
            <FlowbiteButton
                pill={pill}
                id={triggerId}
                size={size}
                color={variant}
                theme={pill ? buttonIconTheme : selectButtonTheme}
                className={buttonClassName}
            >
                {startIcon && <div className='flex flex-shrink-0 justify-flex-start'>{startIcon}</div>}

                <Text variant={valueVariant ?? 'body-large'} className={'flex-1 text-left truncate' + valueClassName}>
                    {
                        value
                            ? hasSelectedItems
                                ? [...middleMenuItems, ...(bottomMenuItems ?? [])]
                                      .filter((item) => item.isSelected)
                                      ?.map((i) => i.value)
                                      .join(', ')
                                : value
                            : null // show nothing
                    }
                </Text>

                <div className='flex items-center gap-1 flex-shrink-0 justify-end'>
                    {onClear && hasSelectedItems && (
                        <div
                            onClick={(e) => {
                                onClear();
                                e.stopPropagation();
                            }}
                            className='flex cursor-pointer'
                        >
                            <IconLoader
                                size='sm'
                                iconName={'IconRemove'}
                                className='stroke-system-on-surface-variant'
                            />
                        </div>
                    )}
                    {endIcon && <div className='flex'>{endIcon}</div>}
                    {arrow && <div className='flex'>{renderArrow(buttonElement)}</div>}
                </div>
            </FlowbiteButton>
        );
    }, [
        id,
        pill,
        size,
        variant,
        buttonClassName,
        startIcon,
        valueVariant,
        valueClassName,
        value,
        hasSelectedItems,
        middleMenuItems,
        bottomMenuItems,
        onClear,
        endIcon,
        arrow,
        renderArrow,
    ]);

    const renderMiddleSelectItem = useCallback(
        (item: SelectMenuItem, textClassName: string) => {
            const handleItemClick = () => {
                onItemSelect?.({ ...item, isSelected: !item.isSelected });
                item.onClick?.();
            };

            const itemDisabled = disableSelectOptions && !item.isSelected;
            const showSelectedCheck = displaySelectedIcon && item.isSelected && !multiSelect;

            return (
                <Dropdown.Item
                    key={item.id}
                    icon={item?.icon}
                    disabled={itemDisabled}
                    className={`${
                        itemDisabled ? 'opacity-50 cursor-default' : 'opacity-100 cursor-pointer'
                    } ${textClassName}`}
                    onClick={handleItemClick}
                >
                    <div
                        className={`justify-between w-full h-full flex flex-row items-center ${
                            itemDisabled ? 'cursor-default' : 'cursor-pointer'
                        }`}
                    >
                        {multiSelect && (
                            <Checkbox
                                id={`${item.id}-check-box`}
                                className={checkboxClass}
                                disabled={itemDisabled}
                                readOnly
                                checked={item.isSelected}
                            />
                        )}
                        <Text variant='label-large'>{item.value}</Text>
                        {showSelectedCheck && (
                            <IconLoader
                                iconName={'IconCheck'}
                                size='sm'
                                data-testid={item.id + '-checkmark'}
                                className='justify-end'
                            />
                        )}
                    </div>
                </Dropdown.Item>
            );
        },
        [disableSelectOptions, displaySelectedIcon, multiSelect, onItemSelect],
    );

    const renderBottomItem = useCallback((item: SelectMenuItem, textClassName: string) => {
        const handleItemClick = () => {
            // Only trigger the action but do not update selected state
            item.onClick?.();
        };

        return (
            <Dropdown.Item key={item.id} className={textClassName} icon={item?.icon} onClick={handleItemClick}>
                <div className='flex items-center w-full justify-between'>
                    <Text variant='title-small'>{item.value}</Text>
                </div>
            </Dropdown.Item>
        );
    }, []);

    return (
        <div className='flex flex-col gap-1'>
            {label && (
                <div className='pb-2'>
                    <Text variant='label-large'>{label}</Text>
                </div>
            )}
            <Dropdown
                disabled={disabled}
                placement={placement}
                data-testid={testId}
                label={value}
                renderTrigger={renderTrigger ?? dropdownRenderButtonTrigger}
                theme={dropdownTheme}
                dismissOnClick={!multiSelect}
            >
                {/* header */}
                {header && (
                    <Dropdown.Header>
                        <Text variant={header.subtitle ? 'label-large' : 'title-medium'}>{header.title}</Text>
                        <Text variant='label-large' className='text-system-on-surface-variant'>
                            {header.subtitle}
                        </Text>
                    </Dropdown.Header>
                )}
                {/* middle */}
                <div className={middleMenuClassName}>
                    {onSelectAll && multiSelect && (
                        <Dropdown.Item onClick={handleSelectAll}>
                            <div className='flex items-center w-full justify-between'>
                                <Checkbox
                                    id={`${id}-select-all-check-box`}
                                    className={checkboxClass}
                                    readOnly
                                    checked={allSelected}
                                />
                                <Text variant='label-large'>Select All</Text>
                            </div>
                        </Dropdown.Item>
                    )}
                    {middleMenuItems.map((item) => renderMiddleSelectItem(item, ''))}
                </div>
                {/* footer */}
                {Array.isArray(bottomMenuItems) && bottomMenuItems.length > 0 && (
                    <>
                        <Dropdown.Divider />
                        {bottomMenuItems.map((item) => renderBottomItem(item, 'text-system-on-surface'))}
                    </>
                )}
            </Dropdown>
            {/* helper text */}
            {helper && (
                <div className='pt-2'>
                    <Text variant='label-large' className='text-system-on-surface-variant'>
                        {helper}
                    </Text>
                </div>
            )}
        </div>
    );
};
